import {
  CollapseOutlineIcon,
  ExpandOutlineIcon,
} from 'app/components/core/icons';
import React from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import MenuDrawerItem from '../MenuDrawerItem';
import { SidebarTooltipStyle } from '../SidebarIcon/styles';
import {
  getDrawerTitle,
  DrawerType,
  type ResolvedMenuDrawerConfig,
  type ResolvedDrawerItem,
} from '../../MenuConfig';
import { trackMenuItemSelected } from '../../trackMenuEvents';
import { isCommunityLink, openCommunity } from 'app/services/community';
import { GroupIdEnum } from 'app/modules/Segment/types';
import * as S from './styles';

interface MenuDrawerProps {
  isOpen: boolean;
  isCollapsed: boolean;
  toggleMenuCollapsed: () => void;
  currentAsideWidth: number;
  menuDrawerExtendedWidth: number;
  menuDrawerCollapsedWidth: number;
  isFloating?: boolean;
  activeDrawerType?: DrawerType;
  drawerConfig: ResolvedMenuDrawerConfig;
  onNavigate: (path: string) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const MenuDrawer = ({
  isOpen,
  isCollapsed,
  toggleMenuCollapsed,
  currentAsideWidth,
  menuDrawerExtendedWidth,
  menuDrawerCollapsedWidth,
  isFloating = false,
  activeDrawerType = DrawerType.PROCESSOS,
  drawerConfig: resolvedDrawerConfig,
  onNavigate,
  onMouseEnter,
  onMouseLeave,
}: MenuDrawerProps) => {
  const currentWidth = isCollapsed
    ? menuDrawerCollapsedWidth
    : menuDrawerExtendedWidth;
  const sections = resolvedDrawerConfig[activeDrawerType] ?? [];

  return (
    <>
      <SidebarTooltipStyle />
      <S.MenuDrawerWrapper
        isOpen={isOpen}
        currentAsideWidth={currentAsideWidth}
        currentWidth={currentWidth}
        isCollapsed={isCollapsed}
        isFloating={isFloating}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <S.MenuDrawerHeader>
          {!isCollapsed && (
            <S.MenuDrawerTitle>
              {getDrawerTitle(activeDrawerType)}
            </S.MenuDrawerTitle>
          )}

          {!isFloating && (
            <S.ToggleButton
              onClick={toggleMenuCollapsed}
              aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
              aria-expanded={!isCollapsed}
              data-tooltip-id="menu-drawer-tooltip"
              data-tooltip-content={isCollapsed ? 'Expandir' : 'Recolher'}
              data-tooltip-place="right"
            >
              {isCollapsed ? (
                <ExpandOutlineIcon size={18} />
              ) : (
                <CollapseOutlineIcon size={18} />
              )}
            </S.ToggleButton>
          )}
        </S.MenuDrawerHeader>

        <S.DrawerNavWrapper isCollapsed={isCollapsed}>
          {sections.map((section) => (
            <S.NavGroup key={section.title} isCollapsed={isCollapsed}>
              {!isCollapsed && <S.NavGroupTitle>{section.title}</S.NavGroupTitle>}
              {section.items
                .filter((item: ResolvedDrawerItem) => item.show !== false)
                .map((item: ResolvedDrawerItem) => {
                  const isExternal =
                    item.externalLink ||
                    item.path.startsWith('http://') ||
                    item.path.startsWith('https://');

                  const handleItemClick = () => {
                    if (isCommunityLink(item.path)) {
                      openCommunity();
                    } else if (isExternal) {
                      window.open(item.path, '_blank', 'noopener,noreferrer');
                    } else {
                      onNavigate(item.path);
                    }
                  };

                  const handleClick = () => {
                    if (item.disabled) return;
                    trackMenuItemSelected({
                      groupId:
                        item.trackEvent?.groupId ?? GroupIdEnum.GENERAL_GROUP,
                      itemName: item.label,
                      categoryParent:
                        item.trackEvent?.categoryParent ??
                        getDrawerTitle(activeDrawerType),
                    });
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      handleItemClick();
                    }
                  };

                  return (
                    <MenuDrawerItem
                      key={item.label}
                      icon={item.icon}
                      onClick={handleClick}
                      isCollapsed={isCollapsed}
                      label={item.label}
                      route={isExternal ? undefined : item.path}
                      activePrefixes={item.activePrefixes}
                      exactMatch={item.exactMatch}
                      toggle={item.toggle}
                      disabled={item.disabled}
                      disabledTooltip={item.disabledTooltip}
                      badge={item.badge}
                      highlight={item.highlight}
                      externalLink={isExternal}
                    />
                  );
                })}
            </S.NavGroup>
          ))}
        </S.DrawerNavWrapper>
      </S.MenuDrawerWrapper>
      <ReactTooltip
        id="menu-drawer-tooltip"
        place="right"
        className="sidebar-custom-tooltip"
      />
      <ReactTooltip
        id="menu-drawer-item-tooltip"
        place="right"
        className="sidebar-custom-tooltip"
      />
    </>
  );
};

export default MenuDrawer;
